document.documentElement.classList.remove('no-js');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const initStickyHeaders = (scope = document) => {
  const headers = scope.matches?.('[data-header]') ? [scope] : [...scope.querySelectorAll('[data-header]')];
  headers.forEach((header) => {
    const wrapper = header.closest('.shopify-section');
    if (wrapper) wrapper.classList.toggle('shopify-section--sticky-header', header.classList.contains('site-header--sticky'));
  });
};

initStickyHeaders();
document.addEventListener('shopify:section:load', (event) => initStickyHeaders(event.target));

const initRotatingSearchPlaceholders = (scope = document) => {
  const inputs = scope.matches?.('[data-rotating-search-placeholder]') ? [scope] : [...scope.querySelectorAll('[data-rotating-search-placeholder]')];
  inputs.forEach((input) => {
    if (input.dataset.rotatingPlaceholderReady === 'true') return;
    input.dataset.rotatingPlaceholderReady = 'true';

    const rawPrefix = (input.dataset.placeholderPrefix || '').trim();
    const prefix = rawPrefix ? `${rawPrefix} ` : '';
    const prompts = [
      input.dataset.placeholderOne,
      input.dataset.placeholderTwo,
      input.dataset.placeholderThree,
      input.dataset.placeholderFour,
      input.dataset.placeholderFive
    ]
      .map((prompt) => (prompt || '').trim())
      .filter(Boolean)
      .map((prompt) => `${prefix}${prompt}`);
    if (!prompts.length) return;

    input.placeholder = prompts[0];
    if (prompts.length < 2 || reducedMotion.matches) return;

    const typingSpeed = Math.max(20, Number(input.dataset.typingSpeed) || 100);
    const deletingSpeed = Math.max(20, Number(input.dataset.deletingSpeed) || 60);
    const firstDelay = Math.max(0, Number(input.dataset.firstDelay) || 2000);
    const holdDelay = Math.max(0, Number(input.dataset.holdDelay) || 2400);
    const betweenDelay = Math.max(0, Number(input.dataset.betweenDelay) || 500);
    const wait = (delay) => new Promise((resolve) => window.setTimeout(resolve, delay));
    let promptIndex = 0;

    const waitUntilAvailable = async () => {
      while (input.isConnected && (input.value !== '' || document.hidden)) await wait(200);
      return input.isConnected;
    };

    const run = async () => {
      await wait(firstDelay);
      while (input.isConnected) {
        if (!(await waitUntilAvailable())) return;
        const nextIndex = (promptIndex + 1) % prompts.length;
        const nextPrompt = prompts[nextIndex];
        let currentPrompt = input.placeholder || prompts[promptIndex];
        let sharedLength = 0;
        while (sharedLength < currentPrompt.length && sharedLength < nextPrompt.length && currentPrompt.charAt(sharedLength) === nextPrompt.charAt(sharedLength)) sharedLength += 1;

        while (input.isConnected && input.value === '' && currentPrompt.length > sharedLength) {
          currentPrompt = currentPrompt.slice(0, -1);
          input.placeholder = currentPrompt;
          await wait(deletingSpeed);
        }
        if (!(await waitUntilAvailable())) return;
        await wait(betweenDelay);

        while (input.isConnected && input.value === '' && currentPrompt.length < nextPrompt.length) {
          currentPrompt = nextPrompt.slice(0, currentPrompt.length + 1);
          input.placeholder = currentPrompt;
          await wait(typingSpeed);
        }
        promptIndex = nextIndex;
        if (input.value !== '') input.placeholder = prompts[promptIndex];
        await wait(holdDelay);
      }
    };

    run();
  });
};

const initAnnouncementRotators = (scope = document) => {
  const rotators = scope.matches?.('[data-announcement-rotator]') ? [scope] : [...scope.querySelectorAll('[data-announcement-rotator]')];
  rotators.forEach((rotator) => {
    if (rotator.dataset.announcementReady === 'true') return;
    rotator.dataset.announcementReady = 'true';
    const slides = [...rotator.querySelectorAll('[data-announcement-slide]')];
    if (slides.length < 2 || reducedMotion.matches) return;

    const delay = Math.max(3000, Number(rotator.dataset.announcementDelay) || 4000);
    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let paused = false;
    const show = (index) => {
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
    };
    const tick = () => {
      if (!paused && !document.hidden) {
        activeIndex = (activeIndex + 1) % slides.length;
        show(activeIndex);
      }
      window.setTimeout(tick, delay);
    };
    rotator.addEventListener('mouseenter', () => { paused = true; });
    rotator.addEventListener('mouseleave', () => { paused = false; });
    rotator.addEventListener('focusin', () => { paused = true; });
    rotator.addEventListener('focusout', () => { paused = false; });
    window.setTimeout(tick, delay);
  });
};

initRotatingSearchPlaceholders();
initAnnouncementRotators();
document.addEventListener('shopify:section:load', (event) => {
  initRotatingSearchPlaceholders(event.target);
  initAnnouncementRotators(event.target);
});

const initPredictiveSearch = (scope = document) => {
  const searches = scope.matches?.('[data-predictive-search]') ? [scope] : [...scope.querySelectorAll('[data-predictive-search]')];
  searches.forEach((search) => {
    if (search.dataset.predictiveReady === 'true') return;
    search.dataset.predictiveReady = 'true';
    const input = search.querySelector('[data-predictive-input]');
    const results = search.querySelector('[data-predictive-results]');
    const endpoint = search.dataset.predictiveUrl;
    if (!input || !results || !endpoint) return;

    let debounceTimer;
    let requestController;

    const close = () => {
      results.hidden = true;
      results.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('predictive-search-open');
    };

    const open = () => {
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      document.body.classList.add('predictive-search-open');
    };

    const getResults = async () => {
      const term = input.value.trim();
      if (term.length < 2) {
        close();
        return;
      }

      requestController?.abort();
      requestController = new AbortController();
      results.innerHTML = '<div class="predictive-search__loading" role="status">Searching Jacamar…</div>';
      open();

      const parameters = new URLSearchParams({
        q: term,
        'resources[type]': 'product',
        'resources[limit]': '4',
        'resources[options][unavailable_products]': 'last',
        section_id: 'predictive-search',
      });

      try {
        const response = await fetch(`${endpoint}?${parameters.toString()}`, { signal: requestController.signal });
        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
        const responseText = await response.text();
        const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
        const renderedSection = responseDocument.querySelector('#shopify-section-predictive-search');
        if (!renderedSection) throw new Error('Predictive search section was not returned.');
        results.innerHTML = renderedSection.innerHTML;
        open();
        document.dispatchEvent(new CustomEvent('jacamar:wishlist-refresh'));
        document.dispatchEvent(new CustomEvent('jacamar:predictive-search:rendered', { detail: { term } }));
      } catch (error) {
        if (error.name === 'AbortError') return;
        close();
      }
    };

    input.addEventListener('input', () => {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(getResults, 180);
    });
    input.addEventListener('focus', () => {
      if (results.innerHTML.trim() !== '' && input.value.trim().length >= 2) open();
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        close();
        input.blur();
      }
    });
    document.addEventListener('click', (event) => {
      if (!search.contains(event.target)) close();
    });
  });
};

initPredictiveSearch();
document.addEventListener('shopify:section:load', (event) => initPredictiveSearch(event.target));

const menuButton = document.querySelector('[data-menu-toggle]');
const mobileMenu = document.querySelector('[data-mobile-menu]');
const mobileSearchButton = document.querySelector('[data-mobile-search-toggle]');
const mobileSearchPanel = document.querySelector('[data-mobile-search-panel]');
const mobileSearchInput = mobileSearchPanel?.querySelector('[data-predictive-input]');

const closeMobileSearch = () => {
  if (!mobileSearchButton || !mobileSearchPanel) return;
  mobileSearchPanel.hidden = true;
  mobileSearchButton.setAttribute('aria-expanded', 'false');
  mobileSearchButton.setAttribute('aria-label', 'Open search');
};

if (mobileSearchButton && mobileSearchPanel) {
  mobileSearchButton.addEventListener('click', () => {
    const open = mobileSearchPanel.hidden;
    mobileSearchPanel.hidden = !open;
    mobileSearchButton.setAttribute('aria-expanded', String(open));
    mobileSearchButton.setAttribute('aria-label', open ? 'Close search' : 'Open search');
    if (open) {
      mobileMenu?.classList.remove('is-open');
      menuButton?.setAttribute('aria-expanded', 'false');
      window.requestAnimationFrame(() => mobileSearchInput?.focus());
    }
  });
}

if (menuButton && mobileMenu) {
  const closeMobileMenu = () => {
    mobileMenu.classList.remove('is-open');
    menuButton.setAttribute('aria-expanded', 'false');
  };
  menuButton.addEventListener('click', () => {
    const open = mobileMenu.classList.toggle('is-open');
    menuButton.setAttribute('aria-expanded', String(open));
    if (open) closeMobileSearch();
  });
  mobileMenu.addEventListener('click', (event) => { if (event.target.closest('a')) closeMobileMenu(); });
  document.addEventListener('click', (event) => { if (!event.target.closest('[data-header]')) closeMobileMenu(); });
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeMobileMenu(); });
}
document.addEventListener('click', (event) => {
  if (!event.target.closest('[data-header]')) closeMobileSearch();
});
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') closeMobileSearch();
});

const initHeroCarousels = (scope = document) => {
  scope.querySelectorAll('[data-hero-carousel]').forEach((carousel) => {
    if (carousel.dataset.carouselReady === 'true') return;
    carousel.dataset.carouselReady = 'true';
    const slides = [...carousel.querySelectorAll('[data-hero-slide]')];
    const dots = [...carousel.querySelectorAll('[data-hero-dot]')];
    if (!slides.length) return;

    let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let hasRenderedSlide = false;
    let timer;
    let pointerStart = null;
    const interval = Math.max(3000, Number(carousel.dataset.interval) || 5000);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplay = slides.length > 1 && carousel.dataset.autoplay === 'true' && !reducedMotion;

    const refreshPreparedSlides = () => {
      const nextIndex = (activeIndex + 1) % slides.length;
      slides.forEach((slide, index) => {
        const keepPrepared = index === activeIndex || index === nextIndex || slide.classList.contains('is-zooming');
        slide.classList.toggle('is-motion-prepared', keepPrepared);
      });
    };

    const primeSlide = (index) => {
      const slide = slides[(index + slides.length) % slides.length];
      const image = slide?.querySelector('[data-hero-motion-layer] img');
      if (!image) return;
      slide.classList.add('is-motion-prepared');
      image.loading = 'eager';
      image.decode?.().catch(() => {});
    };

    slides.forEach((slide) => slide.addEventListener('transitionend', (event) => {
      if (event.propertyName !== 'opacity' || slide.classList.contains('is-active')) return;
      slide.classList.remove('is-zooming');
      refreshPreparedSlides();
    }));

    const showSlide = (index) => {
      if (!Number.isFinite(index)) return;
      const nextIndex = (index + slides.length) % slides.length;
      if (hasRenderedSlide && nextIndex === activeIndex) return;
      activeIndex = nextIndex;
      const incomingSlide = slides[activeIndex];
      if (!incomingSlide.classList.contains('is-zooming')) incomingSlide.classList.add('is-zooming');
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === activeIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-pressed', String(active));
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      primeSlide(activeIndex + 1);
      refreshPreparedSlides();
      carousel.dataset.activeSlide = String(activeIndex);
      carousel.dispatchEvent(new CustomEvent('jacamar:carousel:changed', { bubbles: true, detail: { index: activeIndex } }));
      hasRenderedSlide = true;
    };

    const stop = () => window.clearTimeout(timer);
    const start = () => {
      stop();
      if (autoplay) timer = window.setTimeout(() => { showSlide(activeIndex + 1); start(); }, interval);
    };

    dots.forEach((dot) => dot.addEventListener('click', () => { showSlide(Number(dot.dataset.heroDot)); start(); }));
    carousel.querySelector('[data-hero-prev]')?.addEventListener('click', () => { showSlide(activeIndex - 1); start(); });
    carousel.querySelector('[data-hero-next]')?.addEventListener('click', () => { showSlide(activeIndex + 1); start(); });
    carousel.addEventListener('jacamar:carousel:go-to', (event) => { showSlide(Number(event.detail?.index) || 0); start(); });
    carousel.jacamarCarousel = { goTo: showSlide, next: () => showSlide(activeIndex + 1), previous: () => showSlide(activeIndex - 1), get index() { return activeIndex; } };
    carousel.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' || event.button !== 0) return;
      pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
      stop();
    });
    carousel.addEventListener('pointerup', (event) => {
      if (!pointerStart || pointerStart.id !== event.pointerId) return;
      const deltaX = event.clientX - pointerStart.x;
      const deltaY = event.clientY - pointerStart.y;
      const threshold = Math.max(44, carousel.clientWidth * 0.06);
      if (Math.abs(deltaX) >= threshold && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) showSlide(activeIndex + (deltaX < 0 ? 1 : -1));
      pointerStart = null;
      start();
    });
    carousel.addEventListener('pointercancel', () => { pointerStart = null; start(); });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    });
    showSlide(activeIndex);
    start();
  });
};

initHeroCarousels();
document.addEventListener('shopify:section:load', (event) => initHeroCarousels(event.target));

const initCollectionPages = (scope = document) => {
  scope.querySelectorAll('[data-collection-page]').forEach((page) => {
    if (page.dataset.collectionReady === 'true') return;
    page.dataset.collectionReady = 'true';
    const filterDrawer = page.querySelector('[data-filter-drawer]');
    const filterToggle = page.querySelector('[data-filter-toggle]');
    const desktopFilterToggle = page.querySelector('[data-desktop-filter-toggle]');
    const filterOverlay = page.querySelector('[data-filter-overlay]');
    const filterClose = page.querySelector('[data-filter-close]');
    let filterReturnFocus = null;

    const closeFilters = () => {
      filterDrawer?.classList.remove('is-open');
      filterDrawer?.removeAttribute('role');
      filterDrawer?.removeAttribute('aria-modal');
      filterOverlay?.classList.remove('is-open');
      filterToggle?.setAttribute('aria-expanded', 'false');
      if (page.classList.contains('collection-page--filters-top_bar')) desktopFilterToggle?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('collection-filter-open');
      filterReturnFocus?.focus();
    };
    filterToggle?.addEventListener('click', () => {
      filterReturnFocus = filterToggle;
      filterDrawer?.classList.add('is-open');
      filterDrawer?.setAttribute('role', 'dialog');
      filterDrawer?.setAttribute('aria-modal', 'true');
      filterOverlay?.classList.add('is-open');
      filterToggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('collection-filter-open');
      filterClose?.focus();
    });
    filterClose?.addEventListener('click', closeFilters);
    filterOverlay?.addEventListener('click', closeFilters);
    filterDrawer?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeFilters();
      if (event.key !== 'Tab' || !document.body.classList.contains('collection-filter-open')) return;
      const focusable = [...filterDrawer.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), summary')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && page.classList.contains('collection-page--filters-top_bar') && filterDrawer?.classList.contains('is-open')) closeFilters();
    });

    page.querySelectorAll('[data-collection-filter-form] input[type="checkbox"]').forEach((input) => input.addEventListener('change', () => {
      if (window.matchMedia('(min-width: 761px)').matches) input.form?.requestSubmit();
    }));
    page.querySelectorAll('[data-collection-sort], [data-mobile-sort]').forEach((select) => select.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', select.value);
      window.location.assign(url.toString());
    }));

    const grid = page.querySelector('[data-collection-grid]');
    const layout = page.querySelector('.collection-layout');
    desktopFilterToggle?.addEventListener('click', (event) => {
      filterReturnFocus = desktopFilterToggle;
      if (page.classList.contains('collection-page--filters-top_bar')) {
        const open = filterDrawer?.classList.toggle('is-open');
        event.currentTarget.setAttribute('aria-expanded', String(Boolean(open)));
        if (open) filterDrawer?.querySelector('summary')?.focus();
        return;
      }
      const collapsed = layout?.classList.toggle('filters-collapsed');
      event.currentTarget.setAttribute('aria-expanded', String(!collapsed));
    });
    page.querySelectorAll('[data-grid-columns]').forEach((button) => button.addEventListener('click', () => {
      grid?.style.setProperty('--collection-columns', button.dataset.gridColumns);
      if (grid) grid.dataset.view = 'grid';
      page.querySelectorAll('[data-grid-columns]').forEach((item) => item.classList.toggle('is-active', item === button));
      page.querySelector('[data-collection-view]')?.setAttribute('aria-pressed', 'false');
    }));
    page.querySelector('[data-collection-view]')?.addEventListener('click', (event) => {
      const listActive = grid?.dataset.view !== 'list';
      if (grid) grid.dataset.view = listActive ? 'list' : 'grid';
      event.currentTarget.setAttribute('aria-pressed', String(listActive));
      page.querySelectorAll('[data-grid-columns]').forEach((item) => item.classList.toggle('is-active', !listActive && item.dataset.gridColumns === getComputedStyle(page).getPropertyValue('--collection-columns').trim()));
    });

    const search = page.querySelector('[data-collection-search]');
    const cards = [...page.querySelectorAll('[data-collection-product]')];
    const count = page.querySelector('[data-visible-count]');
    const defaultCountText = count?.textContent.trim() || '';
    const searchEmpty = page.querySelector('[data-collection-search-empty]');
    const updateSearch = () => {
      const term = search.value.trim().toLowerCase();
      let visible = 0;
      cards.forEach((card) => {
        const match = !term || card.dataset.productTitle.includes(term);
        card.hidden = !match;
        if (match) visible += 1;
      });
      if (count) count.textContent = term ? `${visible} product${visible === 1 ? '' : 's'}` : defaultCountText;
      if (searchEmpty) searchEmpty.hidden = visible > 0 || !term;
      if (grid) grid.hidden = visible === 0 && Boolean(term);
    };
    search?.addEventListener('input', updateSearch);
    page.querySelector('[data-collection-search-clear]')?.addEventListener('click', () => {
      search.value = '';
      updateSearch();
      search.focus();
    });

    page.querySelectorAll('.product-card__image:not(.product-card__image--secondary)').forEach((image) => {
      const markImageError = () => image.closest('.product-card__media')?.classList.add('has-image-error');
      image.addEventListener('error', markImageError, { once: true });
      if (image.complete && image.naturalWidth === 0) markImageError();
    });
  });
};

initCollectionPages();
document.addEventListener('shopify:section:load', (event) => initCollectionPages(event.target));

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-quick-view-open]');
  if (opener) document.getElementById(opener.dataset.quickViewOpen)?.showModal();
  if (event.target.matches('.quick-view-dialog')) event.target.close();
});

document.addEventListener('change', (event) => {
  const select = event.target.closest('[data-quick-view-variant]');
  if (!select) return;
  const dialog = select.closest('.quick-view-dialog');
  const selected = select.selectedOptions[0];
  const current = dialog?.querySelector('[data-quick-view-current]');
  const compare = dialog?.querySelector('[data-quick-view-compare]');
  const submit = dialog?.querySelector('[data-quick-view-submit]');
  const submitLabel = dialog?.querySelector('[data-quick-view-submit-label]');
  const stock = dialog?.querySelector('[data-quick-view-stock]');
  if (current) current.textContent = selected.dataset.price || '';
  if (selected.dataset.compare) {
    if (compare) compare.textContent = selected.dataset.compare;
    else if (current) {
      const comparePrice = document.createElement('s');
      comparePrice.dataset.quickViewCompare = '';
      comparePrice.textContent = selected.dataset.compare;
      current.before(comparePrice);
    }
  } else {
    compare?.remove();
  }
  const available = selected.dataset.available === 'true';
  if (submit) submit.disabled = !available;
  if (submitLabel) submitLabel.textContent = available ? 'Add to cart' : 'Sold out';
  if (stock) {
    stock.textContent = available ? 'In stock' : 'Sold out';
    stock.classList.toggle('is-sold-out', !available);
  }
});

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-cart-decrease], [data-cart-increase]');
  if (!button) return;
  const quantity = button.closest('.cart-quantity')?.querySelector('[data-cart-quantity]');
  if (!quantity) return;
  const change = button.matches('[data-cart-increase]') ? 1 : -1;
  quantity.value = Math.max(0, Number(quantity.value || 0) + change);
  quantity.dispatchEvent(new Event('change', { bubbles: true }));
});

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('.quick-add-form');
  if (!form) return;
  event.preventDefault();
  const button = form.querySelector('button');
  const oldLabel = button.textContent;
  button.disabled = true;
  button.textContent = 'Adding…';
  try {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(root + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
    if (!response.ok) throw new Error('Could not add item');
    const cart = await fetch(root + 'cart.js').then((res) => res.json());
    updateCartIndicators(cart);
    document.dispatchEvent(new CustomEvent('jacamar:cart-updated', { detail: { cart } }));
    const toast = document.createElement('div');
    toast.className = 'cart-toast';
    toast.setAttribute('role', 'status');
    toast.textContent = '✓ Added to your cart';
    document.body.appendChild(toast);
    window.setTimeout(() => toast.remove(), 2400);
  } catch {
    window.location.href = form.closest('.product-card').querySelector('h3 a').href;
  } finally {
    button.disabled = false;
    button.textContent = oldLabel;
  }
});

document.addEventListener('submit', async (event) => {
  const form = event.target.closest('.product-form, .quick-view-form');
  if (!form) return;
  if (form.classList.contains('product-form') && event.submitter && !event.submitter.matches('[data-add-to-cart]')) return;
  event.preventDefault();
  const button = event.submitter || form.querySelector('[data-add-to-cart], button[type="submit"]');
  const originalButton = button?.innerHTML;
  if (button) { button.disabled = true; button.textContent = 'Adding…'; }
  try {
    const root = window.Shopify?.routes?.root || '/';
    const productRoot = form.closest('[data-product-root]');
    const selectedVariantId = productRoot?.dataset.selectedVariantId;
    const submission = new FormData(form);
    if (selectedVariantId) submission.set('id', selectedVariantId);
    const response = await fetch(root + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: submission });
    if (!response.ok) throw new Error('Could not add item');
    const cart = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } }).then((item) => item.json());
    updateCartIndicators(cart);
    form.closest('dialog')?.close();
    document.dispatchEvent(new CustomEvent('jacamar:cart-updated', { detail: { cart, openDrawer: true } }));
  } catch {
    const selectedVariantId = form.closest('[data-product-root]')?.dataset.selectedVariantId;
    const variantField = form.querySelector('[data-variant-id]');
    if (selectedVariantId && variantField) variantField.value = selectedVariantId;
    HTMLFormElement.prototype.submit.call(form);
    return;
  } finally {
    if (button) { button.disabled = false; button.innerHTML = originalButton; }
  }
});

document.querySelectorAll('[data-product-root]').forEach((root) => {
  const variantData = JSON.parse(root.querySelector('[data-product-variants]')?.textContent || '{"variants":[]}').variants;
  const form = root.querySelector('.product-form');
  const optionInputs = [...root.querySelectorAll('[data-option-input]')];
  const variantInput = root.querySelector('[data-variant-id]');
  const price = root.querySelector('[data-product-price]');
  const compare = root.querySelector('[data-product-compare]');
  const savings = root.querySelector('[data-product-savings]');
  const savingsValue = root.querySelector('[data-product-savings-value]');
  const sku = root.querySelector('[data-product-sku]');
  const availability = root.querySelector('[data-product-availability]');
  const addButton = root.querySelector('[data-add-to-cart]');
  const mobileBar = document.querySelector('[data-mobile-atc]');
  const thumbnailScroller = root.querySelector('[data-pdp-thumbs]');
  const thumbnailScrollCue = root.querySelector('[data-thumb-scroll-cue]');
  const mobileGallery = root.querySelector('[data-mobile-gallery]');
  const mobileThumbScroller = root.querySelector('[data-mobile-thumbs]');
  const mobileMedia = [...root.querySelectorAll('[data-mobile-media]')];
  const mobileThumbs = [...root.querySelectorAll('[data-mobile-thumb]')];
  const mobilePrevious = root.querySelector('[data-mobile-gallery-previous]');
  const mobileNext = root.querySelector('[data-mobile-gallery-next]');
  const mobileExpand = root.querySelector('[data-mobile-gallery-expand]');
  const delivery = root.querySelector('[data-estimated-delivery]');

  const parseDeliveryDate = (value) => {
    const parts = String(value || '').split('-').map(Number);
    const date = parts.length === 3 && parts.every(Number.isFinite)
      ? new Date(parts[0], parts[1] - 1, parts[2], 12)
      : new Date();
    date.setHours(12, 0, 0, 0);
    return date;
  };

  const addBusinessDays = (start, amount) => {
    const date = new Date(start);
    let remaining = Math.max(0, Math.floor(Number(amount) || 0));
    while (remaining > 0) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6) remaining -= 1;
    }
    return date;
  };

  const formatDeliveryRange = (start, end) => {
    const month = (date) => new Intl.DateTimeFormat('en-US', { month: 'short' }).format(date);
    const sameYear = start.getFullYear() === end.getFullYear();
    const sameMonth = sameYear && start.getMonth() === end.getMonth();
    if (sameMonth) return `${month(start)} ${start.getDate()}–${end.getDate()}`;
    if (sameYear) return `${month(start)} ${start.getDate()}–${month(end)} ${end.getDate()}`;
    return `${month(start)} ${start.getDate()}, ${start.getFullYear()}–${month(end)} ${end.getDate()}, ${end.getFullYear()}`;
  };

  const updateDelivery = (variant) => {
    if (!delivery) return;
    const minDays = Math.max(1, Math.floor(Number(delivery.dataset.minBusinessDays) || 7));
    const maxDays = Math.max(minDays, Math.floor(Number(delivery.dataset.maxBusinessDays) || 12));
    const startDate = parseDeliveryDate(delivery.dataset.startDate);
    const range = delivery.querySelector('[data-delivery-range]');
    if (range) range.textContent = formatDeliveryRange(addBusinessDays(startDate, minDays), addBusinessDays(startDate, maxDays));

    const label = delivery.querySelector('[data-delivery-shipping-label]');
    if (!label) return;
    const enabled = delivery.dataset.freeShippingEnabled === 'true';
    const universal = delivery.dataset.freeShippingMode === 'universal';
    const qualifies = Number(variant?.price || 0) >= Number(delivery.dataset.freeShippingThreshold || 0);
    label.textContent = !enabled
      ? delivery.dataset.neutralLabel
      : (universal || qualifies ? delivery.dataset.freeLabel : delivery.dataset.paidLabel);
  };

  const updateThumbnailScrollCue = () => {
    if (!thumbnailScroller || !thumbnailScrollCue) return;
    const hasOverflow = thumbnailScroller.scrollHeight > thumbnailScroller.clientHeight + 2;
    const isAtEnd = thumbnailScroller.scrollTop + thumbnailScroller.clientHeight >= thumbnailScroller.scrollHeight - 3;
    thumbnailScrollCue.hidden = !hasOverflow || isAtEnd;
  };

  thumbnailScroller?.addEventListener('scroll', updateThumbnailScrollCue, { passive: true });
  if (thumbnailScroller && 'ResizeObserver' in window) new ResizeObserver(updateThumbnailScrollCue).observe(thumbnailScroller);
  window.requestAnimationFrame(updateThumbnailScrollCue);

  const selectedOptions = () => [...root.querySelectorAll('[data-option-position]')].map((group) => group.querySelector('[data-option-input]:checked')?.value);
  const findVariant = (options) => variantData.find((variant) => variant.options.every((option, index) => option === options[index]));

  const activeMobileIndex = () => Math.max(0, mobileThumbs.findIndex((thumb) => thumb.classList.contains('active')));
  const updateMobileGalleryControls = (index) => {
    if (mobilePrevious) mobilePrevious.disabled = index <= 0;
    if (mobileNext) mobileNext.disabled = index >= mobileMedia.length - 1;
  };

  const setActiveMobileThumb = (mediaId) => {
    let activeIndex = 0;
    mobileThumbs.forEach((thumb, index) => {
      const active = thumb.dataset.mobileThumb === String(mediaId);
      if (active) activeIndex = index;
      thumb.classList.toggle('active', active);
      thumb.setAttribute('aria-pressed', String(active));
      if (active && mobileThumbScroller) {
        mobileThumbScroller.scrollTo({
          left: thumb.offsetLeft - mobileThumbScroller.offsetLeft - ((mobileThumbScroller.clientWidth - thumb.offsetWidth) / 2),
          behavior: 'smooth'
        });
      }
    });
    updateMobileGalleryControls(activeIndex);
  };

  const scrollToMobileMedia = (mediaId, behavior = 'smooth') => {
    if (!mobileGallery || !mediaId) return;
    const item = mobileMedia.find((media) => media.dataset.mobileMedia === String(mediaId));
    if (!item) return;
    mobileGallery.scrollTo({ left: item.offsetLeft - mobileGallery.offsetLeft, behavior });
    setActiveMobileThumb(mediaId);
  };

  let mobileScrollFrame;
  mobileGallery?.addEventListener('scroll', () => {
    window.cancelAnimationFrame(mobileScrollFrame);
    mobileScrollFrame = window.requestAnimationFrame(() => {
      const center = mobileGallery.scrollLeft + (mobileGallery.clientWidth / 2);
      const closest = mobileMedia.reduce((current, item) => {
        const itemCenter = item.offsetLeft + (item.offsetWidth / 2);
        const currentCenter = current.offsetLeft + (current.offsetWidth / 2);
        return Math.abs(itemCenter - center) < Math.abs(currentCenter - center) ? item : current;
      }, mobileMedia[0]);
      if (closest) setActiveMobileThumb(closest.dataset.mobileMedia);
    });
  }, { passive: true });

  mobileThumbs.forEach((thumb) => thumb.addEventListener('click', () => scrollToMobileMedia(thumb.dataset.mobileThumb)));
  mobilePrevious?.addEventListener('click', () => {
    const target = mobileMedia[activeMobileIndex() - 1];
    if (target) scrollToMobileMedia(target.dataset.mobileMedia);
  });
  mobileNext?.addEventListener('click', () => {
    const target = mobileMedia[activeMobileIndex() + 1];
    if (target) scrollToMobileMedia(target.dataset.mobileMedia);
  });
  mobileExpand?.addEventListener('click', () => {
    mobileMedia[activeMobileIndex()]?.querySelector('[data-zoom-trigger]')?.click();
  });
  updateMobileGalleryControls(activeMobileIndex());

  const showMedia = (mediaId) => {
    if (!mediaId) return;
    root.querySelector(`[data-media-thumb="${mediaId}"]`)?.click();
    scrollToMobileMedia(mediaId);
  };

  const updateProduct = ({ syncMedia = true, updateUrl = true } = {}) => {
    const options = optionInputs.length ? selectedOptions() : (variantData[0]?.options || []);
    const variant = findVariant(options);
    root.querySelectorAll('[data-option-position]').forEach((group, groupIndex) => {
      const selected = group.querySelector('[data-option-input]:checked');
      const output = group.querySelector('[data-selected-option]');
      if (output && selected) output.textContent = selected.value;
      group.querySelectorAll('[data-option-input]').forEach((input) => {
        const candidate = [...options];
        candidate[groupIndex] = input.value;
        const compatibleAvailable = variantData.some((item) => item.available && item.options.every((option, index) => option === candidate[index]));
        const globallyAvailable = variantData.some((item) => item.available && item.options[groupIndex] === input.value);
        const label = input.nextElementSibling;
        if (label) {
          label.classList.toggle('is-unavailable', !compatibleAvailable);
          label.hidden = !globallyAvailable && !input.checked;
        }
        input.setAttribute('aria-disabled', String(!globallyAvailable));
      });
    });
    const canBuy = Boolean(variant?.available);
    if (variantInput) {
      const nextVariantId = variant?.id ? String(variant.id) : '';
      root.dataset.selectedVariantId = nextVariantId;
      variantInput.setAttribute('value', nextVariantId);
      variantInput.value = nextVariantId;
    }
    if (price) price.textContent = variant?.price_formatted || '';
    if (compare) {
      const onSale = variant && variant.compare_at_price > variant.price;
      compare.hidden = !onSale;
      compare.textContent = onSale ? variant.compare_at_price_formatted : '';
      if (savings) savings.hidden = !onSale;
      if (savingsValue && onSale) {
        const percentage = `${variant.savings_percentage}%`;
        if (root.dataset.savingsMode === 'percentage') savingsValue.textContent = percentage;
        else if (root.dataset.savingsMode === 'both') savingsValue.textContent = `${variant.savings_formatted} (${percentage})`;
        else savingsValue.textContent = variant.savings_formatted;
      }
    }
    if (sku) sku.textContent = variant?.sku ? `SKU: ${variant.sku}` : '';
    if (availability) {
      availability.classList.toggle('available', canBuy);
      availability.classList.toggle('unavailable', !canBuy);
      const label = availability.querySelector('b');
      if (label) label.textContent = canBuy ? 'In stock' : 'This combination is unavailable';
    }
    if (addButton) {
      addButton.disabled = !canBuy;
      addButton.querySelector('span').textContent = canBuy ? 'Add to cart' : 'Unavailable';
    }
    if (mobileBar) {
      const mobilePrice = mobileBar.querySelector('[data-mobile-price]');
      const mobileAdd = mobileBar.querySelector('[data-mobile-add]');
      if (mobilePrice) mobilePrice.textContent = variant?.price_formatted || '';
      if (mobileAdd) { mobileAdd.disabled = !canBuy; mobileAdd.textContent = canBuy ? 'Add to cart' : 'Unavailable'; }
    }
    updateDelivery(variant);
    if (syncMedia && variant?.featured_media_id) showMedia(variant.featured_media_id);
    if (updateUrl && variant?.id) {
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.set('variant', variant.id);
      history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
    }
  };

  optionInputs.forEach((input) => input.addEventListener('change', () => updateProduct({ syncMedia: true, updateUrl: true })));
  root.querySelector('[data-qty-minus]')?.addEventListener('click', () => { const input = root.querySelector('[data-quantity]'); input.value = Math.max(1, Number(input.value) - 1); });
  root.querySelector('[data-qty-plus]')?.addEventListener('click', () => { const input = root.querySelector('[data-quantity]'); input.value = Number(input.value) + 1; });
  mobileBar?.querySelector('[data-mobile-add]')?.addEventListener('click', () => {
    if (form && addButton && !addButton.disabled) form.requestSubmit(addButton);
  });

  if (mobileBar && addButton) {
    let stickyFrame;
    const stickyButton = mobileBar.querySelector('[data-mobile-add]');
    mobileBar.hidden = false;
    const updateStickyBarVisibility = () => {
      const rect = addButton.getBoundingClientRect();
      const visible = rect.bottom <= 0;
      mobileBar.classList.toggle('is-visible', visible);
      mobileBar.setAttribute('aria-hidden', String(!visible));
      if (stickyButton) stickyButton.tabIndex = visible ? 0 : -1;
    };
    const queueStickyBarUpdate = () => {
      window.cancelAnimationFrame(stickyFrame);
      stickyFrame = window.requestAnimationFrame(updateStickyBarVisibility);
    };
    const stickyObserver = new IntersectionObserver(queueStickyBarUpdate, { threshold: [0, 0.01] });
    stickyObserver.observe(addButton);
    window.addEventListener('scroll', queueStickyBarUpdate, { passive: true });
    window.addEventListener('resize', queueStickyBarUpdate);
    window.requestAnimationFrame(updateStickyBarVisibility);
  }

  root.querySelectorAll('[data-media-thumb]').forEach((thumb) => thumb.addEventListener('click', () => {
    const id = thumb.dataset.mediaThumb;
    root.querySelectorAll('[data-media-thumb]').forEach((item) => { item.classList.toggle('active', item === thumb); item.setAttribute('aria-pressed', String(item === thumb)); });
    root.querySelectorAll('[data-main-media]').forEach((item) => { const active = item.dataset.mainMedia === id; item.hidden = !active; item.classList.toggle('active', active); });
    thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    window.requestAnimationFrame(updateThumbnailScrollCue);
  }));
  // Preserve the Liquid-selected hero image on first paint. Variant imagery
  // starts following the customer only after they deliberately change an option.
  updateProduct({ syncMedia: false, updateUrl: false });
});

const zoomDialog = document.querySelector('[data-zoom-dialog]');
const productZoomItems = [...document.querySelectorAll('[data-main-media] [data-zoom-trigger]')].map((trigger) => ({
  index: Number(trigger.dataset.zoomIndex || 0),
  src: trigger.dataset.zoomSrc || trigger.querySelector('img')?.currentSrc || trigger.querySelector('img')?.src,
  alt: trigger.dataset.zoomAlt || trigger.querySelector('img')?.alt || ''
})).filter((item) => item.src);
let activeZoomIndex = 0;
let zoomReturnFocus = null;
const renderZoomItem = (index) => {
  if (!zoomDialog || !productZoomItems.length) return;
  activeZoomIndex = (index + productZoomItems.length) % productZoomItems.length;
  const item = productZoomItems[activeZoomIndex];
  const image = zoomDialog.querySelector('[data-zoom-image]');
  const counter = zoomDialog.querySelector('[data-zoom-counter]');
  if (image) { image.src = item.src; image.alt = item.alt; }
  if (counter) counter.textContent = `${activeZoomIndex + 1} / ${productZoomItems.length}`;
  zoomDialog.querySelector('[data-zoom-previous]')?.toggleAttribute('hidden', productZoomItems.length < 2);
  zoomDialog.querySelector('[data-zoom-next]')?.toggleAttribute('hidden', productZoomItems.length < 2);
};
const openZoom = (trigger) => {
  if (!zoomDialog || !trigger) return;
  zoomReturnFocus = trigger;
  const requestedIndex = Number(trigger.dataset.zoomIndex || 0);
  const resolvedIndex = productZoomItems.findIndex((item) => item.index === requestedIndex);
  renderZoomItem(resolvedIndex < 0 ? 0 : resolvedIndex);
  zoomDialog.showModal();
};
const closeZoom = () => {
  if (!zoomDialog?.open) return;
  zoomDialog.close();
  zoomReturnFocus?.focus?.({ preventScroll: true });
};
document.querySelectorAll('[data-zoom-trigger]').forEach((trigger) => trigger.addEventListener('click', () => openZoom(trigger)));
document.querySelectorAll('[data-detail-zoom]').forEach((button) => button.addEventListener('click', () => {
  const image = button.querySelector('img');
  if (!zoomDialog || !image) return;
  zoomReturnFocus = button;
  const target = zoomDialog.querySelector('[data-zoom-image]');
  const counter = zoomDialog.querySelector('[data-zoom-counter]');
  if (target) { target.src = image.currentSrc || image.src; target.alt = image.alt || ''; }
  if (counter) counter.textContent = '';
  zoomDialog.querySelector('[data-zoom-previous]')?.setAttribute('hidden', '');
  zoomDialog.querySelector('[data-zoom-next]')?.setAttribute('hidden', '');
  zoomDialog.showModal();
}));
zoomDialog?.querySelector('[data-zoom-close]')?.addEventListener('click', closeZoom);
zoomDialog?.querySelector('[data-zoom-previous]')?.addEventListener('click', () => renderZoomItem(activeZoomIndex - 1));
zoomDialog?.querySelector('[data-zoom-next]')?.addEventListener('click', () => renderZoomItem(activeZoomIndex + 1));
zoomDialog?.addEventListener('click', (event) => { if (event.target === zoomDialog) closeZoom(); });
zoomDialog?.addEventListener('close', () => zoomReturnFocus?.focus?.({ preventScroll: true }));
zoomDialog?.addEventListener('keydown', (event) => {
  if (event.key === 'ArrowLeft') { event.preventDefault(); renderZoomItem(activeZoomIndex - 1); }
  if (event.key === 'ArrowRight') { event.preventDefault(); renderZoomItem(activeZoomIndex + 1); }
});
let zoomTouchStartX = null;
zoomDialog?.querySelector('[data-zoom-stage]')?.addEventListener('touchstart', (event) => { zoomTouchStartX = event.changedTouches[0]?.clientX ?? null; }, { passive: true });
zoomDialog?.querySelector('[data-zoom-stage]')?.addEventListener('touchend', (event) => {
  if (zoomTouchStartX === null || productZoomItems.length < 2) return;
  const distance = (event.changedTouches[0]?.clientX ?? zoomTouchStartX) - zoomTouchStartX;
  if (Math.abs(distance) > 45) renderZoomItem(activeZoomIndex + (distance < 0 ? 1 : -1));
  zoomTouchStartX = null;
}, { passive: true });

document.querySelectorAll('[data-product-information]').forEach((section) => {
  const tabs = [...section.querySelectorAll('[data-info-tab]')];
  const panels = [...section.querySelectorAll('[data-info-panel]')];
  const mobileQuery = window.matchMedia('(max-width: 960px)');
  const animateContent = (content, open, done) => {
    content._pdpAnimation?.cancel();
    if (reducedMotion.matches || !content.animate) { content.style.overflow = ''; done(); return; }
    const styles = window.getComputedStyle(content);
    const height = open ? content.scrollHeight : content.getBoundingClientRect().height;
    const openFrame = {
      height: `${height}px`, opacity: 1, transform: 'translateY(0)',
      paddingTop: styles.paddingTop, paddingBottom: styles.paddingBottom,
      borderTopWidth: styles.borderTopWidth, borderBottomWidth: styles.borderBottomWidth
    };
    const closedFrame = {
      height: '0px', opacity: 0, transform: 'translateY(-5px)',
      paddingTop: '0px', paddingBottom: '0px',
      borderTopWidth: '0px', borderBottomWidth: '0px'
    };
    content.style.overflow = 'hidden';
    const animation = content.animate(open ? [closedFrame, openFrame] : [openFrame, closedFrame],
      { duration: open ? 280 : 240, easing: 'cubic-bezier(.2,.7,.2,1)' });
    content._pdpAnimation = animation;
    animation.onfinish = () => { content.style.overflow = ''; content._pdpAnimation = null; done(); };
  };
  const setPanelState = (panel, open, withMotion = false) => {
    const content = panel.querySelector('.info-panel-content');
    const wasOpen = !panel.hidden;
    content?._pdpAnimation?.cancel();
    if (open) panel.hidden = false;
    const toggle = panel.querySelector('[data-info-toggle]');
    if (toggle) {
      toggle.setAttribute('aria-expanded', String(open));
      toggle.querySelector('b').textContent = open ? '−' : '+';
    }
    if (withMotion && mobileQuery.matches && content && wasOpen !== open) {
      animateContent(content, open, () => { if (!open) panel.hidden = true; });
    } else {
      if (content) content.style.overflow = '';
      panel.hidden = !open;
    }
  };
  const activate = (id, focus = false) => {
    tabs.forEach((tab) => { const active = tab.dataset.infoTab === id; tab.setAttribute('aria-selected', String(active)); tab.tabIndex = active ? 0 : -1; if (active && focus) tab.focus(); });
    panels.forEach((panel) => setPanelState(panel, panel.dataset.infoPanel === id));
  };
  const syncResponsiveState = () => {
    if (mobileQuery.matches) {
      panels.forEach((panel) => setPanelState(panel, panel.dataset.mobileDefaultOpen === 'true'));
    } else {
      const activeTab = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];
      if (activeTab) activate(activeTab.dataset.infoTab);
    }
  };
  tabs.forEach((tab, index) => {
    tab.addEventListener('click', () => activate(tab.dataset.infoTab));
    tab.addEventListener('keydown', (event) => {
      let next = index;
      if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
      else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = tabs.length - 1;
      else return;
      event.preventDefault(); activate(tabs[next].dataset.infoTab, true);
    });
  });
  section.querySelectorAll('[data-info-toggle]').forEach((toggle) => toggle.addEventListener('click', () => {
    const panel = toggle.closest('[data-info-panel]');
    const open = toggle.getAttribute('aria-expanded') === 'true';
    setPanelState(panel, !open, true);
  }));
  section.querySelectorAll('[data-faq-toggle]').forEach((toggle) => toggle.addEventListener('click', () => {
    const answer = toggle.nextElementSibling;
    const open = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!open));
    toggle.querySelector('span').textContent = open ? '+' : '−';
    answer._pdpAnimation?.cancel();
    if (open) {
      animateContent(answer, false, () => { answer.hidden = true; });
    } else {
      answer.hidden = false;
      animateContent(answer, true, () => {});
    }
  }));
  syncResponsiveState();
  mobileQuery.addEventListener('change', syncResponsiveState);
});

document.querySelectorAll('[data-promise-carousel]').forEach((carousel) => {
  const track = carousel.querySelector('[data-promise-track]');
  const slides = [...carousel.querySelectorAll('[data-promise-slide]')];
  const previous = carousel.querySelector('[data-promise-previous]');
  const next = carousel.querySelector('[data-promise-next]');
  const current = carousel.querySelector('[data-promise-current]');
  if (!track || slides.length < 1) return;
  const seconds = Number(carousel.dataset.autoplaySeconds);
  const autoplayEnabled = Number.isFinite(seconds) && seconds >= 3 && !reducedMotion.matches && slides.length > 1;
  let clone = null;
  if (autoplayEnabled) {
    clone = slides[0].cloneNode(true);
    clone.removeAttribute('data-promise-slide');
    clone.setAttribute('aria-hidden', 'true');
    clone.inert = true;
    track.appendChild(clone);
  }
  let timer;
  let resumeTimer;
  let inView = false;
  let programmatic = false;
  let hovered = false;
  const activeIndex = () => {
    const trackCenter = track.scrollLeft + (track.clientWidth / 2);
    let closestIndex = 0;
    let closestDistance = Infinity;
    slides.forEach((slide, index) => {
      const slideCenter = slide.offsetLeft + (slide.offsetWidth / 2);
      const distance = Math.abs(trackCenter - slideCenter);
      if (distance < closestDistance) { closestDistance = distance; closestIndex = index; }
    });
    return closestIndex;
  };
  const update = () => {
    const index = activeIndex();
    if (current) current.textContent = String(index + 1);
    if (previous) previous.disabled = index === 0;
    if (next) next.disabled = slides.length < 2 || (!clone && index === slides.length - 1);
  };
  const scrollToSlide = (slide) => {
    if (!slide) return;
    programmatic = true;
    track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
  };
  const go = (direction) => {
    const index = activeIndex();
    if ((direction < 0 && index === 0) || (direction > 0 && index === slides.length - 1 && !clone)) return;
    scrollToSlide(direction > 0 && index === slides.length - 1 ? (clone || slides[0]) : slides[index + direction]);
  };
  const schedule = () => {
    window.clearTimeout(timer);
    if (!autoplayEnabled || !inView || hovered || document.hidden || resumeTimer) return;
    timer = window.setTimeout(() => { go(1); schedule(); }, seconds * 1000);
  };
  const pauseAfterInteraction = () => {
    window.clearTimeout(timer);
    window.clearTimeout(resumeTimer);
    resumeTimer = window.setTimeout(() => { resumeTimer = null; schedule(); }, 8000);
  };
  const settleLoop = () => {
    programmatic = false;
    if (clone && track.scrollLeft >= clone.offsetLeft - track.offsetLeft - 3) {
      track.scrollTo({ left: 0, behavior: 'instant' });
    }
    update();
  };
  previous?.addEventListener('click', () => { pauseAfterInteraction(); go(-1); });
  next?.addEventListener('click', () => { pauseAfterInteraction(); go(1); });
  track.addEventListener('pointerdown', pauseAfterInteraction, { passive: true });
  track.addEventListener('keydown', pauseAfterInteraction);
  track.addEventListener('scrollend', settleLoop);
  track.addEventListener('mouseenter', () => { hovered = true; window.clearTimeout(timer); });
  track.addEventListener('mouseleave', () => { hovered = false; schedule(); });
  let scrollFrame;
  track.addEventListener('scroll', () => {
    window.cancelAnimationFrame(scrollFrame);
    scrollFrame = window.requestAnimationFrame(update);
  }, { passive: true });
  document.addEventListener('visibilitychange', schedule);
  if ('IntersectionObserver' in window) {
    new IntersectionObserver((entries) => { inView = entries[0].isIntersecting; schedule(); }, { threshold: 0.15 }).observe(carousel);
  } else { inView = true; schedule(); }
  update();
});

document.querySelectorAll('[data-product-recommendations]').forEach(async (section) => {
  if (section.querySelector('.product-grid') || !section.dataset.url) return;
  try {
    const html = await fetch(section.dataset.url).then((response) => response.text());
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const replacement = parsed.querySelector('[data-product-recommendations]');
    if (replacement?.innerHTML.trim()) { section.innerHTML = replacement.innerHTML; document.dispatchEvent(new Event('jacamar:wishlist-refresh')); }
  } catch { /* Recommendations remain hidden when unavailable. */ }
});

const cartRoot = () => window.Shopify?.routes?.root || '/';
const fetchCart = () => fetch(cartRoot() + 'cart.js', { headers: { Accept: 'application/json' } }).then((response) => response.json());
const protectionDismissalKey = (variantId) => `jacamarProtectionDismissed:${variantId}`;
const protectionIsDismissed = (variantId) => {
  try { return window.sessionStorage.getItem(protectionDismissalKey(variantId)) === 'true'; }
  catch { return false; }
};
const setProtectionDismissed = (variantId, dismissed) => {
  if (!variantId) return;
  try {
    if (dismissed) window.sessionStorage.setItem(protectionDismissalKey(variantId), 'true');
    else window.sessionStorage.removeItem(protectionDismissalKey(variantId));
  } catch { /* Cart behavior still works when session storage is unavailable. */ }
};
const visibleCartItemCount = (cart) => {
  if (document.body.dataset.hideProtectionLine !== 'true') return cart.item_count;
  const protectionVariantId = Number(document.body.dataset.protectionVariantId || 0);
  if (!protectionVariantId) return cart.item_count;
  const protectionQuantity = cart.items
    .filter((item) => Number(item.variant_id) === protectionVariantId)
    .reduce((total, item) => total + item.quantity, 0);
  return Math.max(0, cart.item_count - protectionQuantity);
};
const updateCartIndicators = (cart) => {
  const itemCount = visibleCartItemCount(cart);
  document.querySelectorAll('[data-cart-count]').forEach((count) => {
    count.textContent = itemCount;
    count.hidden = itemCount < 1;
  });
  document.querySelectorAll('[data-cart-link]').forEach((link) => link.setAttribute('aria-label', `Cart, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`));
  document.querySelectorAll('[data-cart-link] em').forEach((label) => { label.textContent = `Cart (${itemCount})`; });
};

let cartPageQuantityTimer;
const initCartPage = (root = document.querySelector('[data-cart-page]')) => root?.classList.add('cart-page--enhanced');
const changeCartPageLine = async (input) => {
  const root = input.closest('[data-cart-page]');
  const row = input.closest('[data-cart-line]');
  const lineKey = row?.dataset.cartLineKey;
  const sectionId = root?.dataset.cartSectionId;
  const quantity = Math.max(0, Math.floor(Number(input.value || 0)));
  if (!root || !lineKey || !sectionId || root.dataset.cartUpdating === 'true') return;
  input.value = quantity;
  const noteValue = root.querySelector('[name="note"]')?.value || '';
  root.dataset.cartUpdating = 'true';
  root.classList.add('cart-page--updating');
  root.setAttribute('aria-busy', 'true');
  root.querySelectorAll('[data-cart-decrease], [data-cart-increase], [data-cart-quantity]').forEach((control) => { control.disabled = true; });
  try {
    const response = await fetch(cartRoot() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity })
    });
    if (!response.ok) throw new Error('Cart could not be updated');
    const cart = await response.json();
    updateCartIndicators(cart);
    const sectionResponse = await fetch(`${cartRoot()}cart?sections=${encodeURIComponent(sectionId)}`, { headers: { Accept: 'application/json' } });
    if (!sectionResponse.ok) throw new Error('Cart could not be refreshed');
    const sections = await sectionResponse.json();
    const parsed = new DOMParser().parseFromString(sections[sectionId] || '', 'text/html');
    const replacement = parsed.querySelector('[data-cart-page]');
    if (!replacement) throw new Error('Cart could not be refreshed');
    const replacementNote = replacement.querySelector('[name="note"]');
    if (replacementNote) replacementNote.value = noteValue;
    root.replaceWith(replacement);
    initCartPage(replacement);
    initPackageProtection(replacement);
    document.dispatchEvent(new CustomEvent('jacamar:cart-updated', { detail: { cart, openDrawer: false } }));
  } catch {
    window.location.assign(cartRoot() + 'cart');
  }
};
const queueCartPageLineChange = (input, immediate = false) => {
  window.clearTimeout(cartPageQuantityTimer);
  cartPageQuantityTimer = window.setTimeout(() => changeCartPageLine(input), immediate ? 0 : 450);
};
initCartPage();
document.addEventListener('input', (event) => {
  const input = event.target.closest?.('[data-cart-page] [data-cart-quantity]');
  if (input) queueCartPageLineChange(input);
});
document.addEventListener('change', (event) => {
  const input = event.target.closest?.('[data-cart-page] [data-cart-quantity]');
  if (input) queueCartPageLineChange(input, true);
});
const protectionComponents = (variantId) => [...document.querySelectorAll(`[data-package-protection][data-protection-variant-id="${variantId}"]`)];

const setProtectionLoading = (component, loading) => {
  component.classList.toggle('is-loading', loading);
  const input = component.querySelector('[data-protection-toggle]');
  if (input) input.disabled = loading;
};

const syncProtectionComponents = (cart, variantId) => {
  const quantity = cart.items.filter((item) => Number(item.variant_id) === variantId).reduce((total, item) => total + item.quantity, 0);
  protectionComponents(variantId).forEach((component) => {
    component.dataset.protectionInCart = String(quantity > 0);
    component.dataset.protectionQuantity = String(quantity);
    const input = component.querySelector('[data-protection-toggle]');
    if (input) input.checked = quantity > 0;
    setProtectionLoading(component, false);
  });
};

const changeProtection = async (component, enabled, { automatic = false } = {}) => {
  if (window.__jacamarProtectionUpdating) return;
  const variantId = Number(component.dataset.protectionVariantId);
  if (!variantId) return;
  if (!automatic) setProtectionDismissed(variantId, !enabled);
  if (automatic && protectionIsDismissed(variantId) && component.dataset.protectionInCart !== 'true') return;
  window.__jacamarProtectionUpdating = true;
  protectionComponents(variantId).forEach((item) => setProtectionLoading(item, true));
  try {
    let cart = await fetchCart();
    const lines = cart.items.filter((item) => Number(item.variant_id) === variantId);
    const merchandiseLines = cart.items.filter((item) => Number(item.variant_id) !== variantId && item.quantity > 0);
    const dismissed = protectionIsDismissed(variantId);
    const shouldEnable = enabled && merchandiseLines.length > 0 && !(automatic && dismissed);
    let response;
    if (shouldEnable && lines.length === 0) {
      response = await fetch(cartRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: { _jacamar_package_protection: 'true' } }] })
      });
      if (!response.ok) throw new Error('Protection product is unavailable');
    } else if (shouldEnable && (lines.length > 1 || lines[0]?.quantity !== 1)) {
      const updates = {};
      lines.forEach((item, index) => { updates[item.key] = index === 0 ? 1 : 0; });
      response = await fetch(cartRoot() + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates }) });
      if (!response.ok) throw new Error('Could not synchronize protection');
    } else if (!shouldEnable && lines.length > 0) {
      const updates = {};
      lines.forEach((item) => { updates[item.key] = 0; });
      response = await fetch(cartRoot() + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates }) });
      if (!response.ok) throw new Error('Could not remove protection');
    }
    cart = await fetchCart();
    updateCartIndicators(cart);
    syncProtectionComponents(cart, variantId);
    if (response) {
      if (document.querySelector('[data-cart-page]')) window.location.reload();
      else document.dispatchEvent(new CustomEvent('jacamar:cart-updated', { detail: { cart, openDrawer: !automatic } }));
    } else if (enabled && !shouldEnable && !automatic) {
      const message = component.querySelector('[data-protection-message]');
      if (message) message.textContent = 'Add a product before enabling package protection.';
    }
  } catch (error) {
    const message = component.querySelector('[data-protection-message]');
    if (message) message.textContent = error.message || 'Package protection could not be updated.';
    const input = component.querySelector('[data-protection-toggle]');
    if (input) input.checked = component.dataset.protectionInCart === 'true';
    protectionComponents(variantId).forEach((item) => setProtectionLoading(item, false));
  } finally {
    window.__jacamarProtectionUpdating = false;
  }
};

const initPackageProtection = (scope = document) => {
  const components = scope.matches?.('[data-package-protection]') ? [scope] : [...scope.querySelectorAll('[data-package-protection]')];
  components.forEach((component) => {
    if (component.dataset.protectionReady === 'true') return;
    component.dataset.protectionReady = 'true';
    const toggle = component.querySelector('[data-protection-toggle]');
    toggle?.addEventListener('change', () => changeProtection(component, toggle.checked, { automatic: false }));
    const protectionInCart = component.dataset.protectionInCart === 'true';
    const defaultOn = component.dataset.protectionDefaultOn === 'true';
    if (protectionInCart || (defaultOn && !protectionIsDismissed(Number(component.dataset.protectionVariantId)))) changeProtection(component, true, { automatic: true });
  });
};

initPackageProtection();
document.addEventListener('shopify:section:load', (event) => initPackageProtection(event.target));
document.addEventListener('click', (event) => {
  const remove = event.target.closest('[data-protection-remove]');
  if (remove) setProtectionDismissed(Number(remove.dataset.protectionVariantId), true);
});

const initCartDrawer = (scope = document) => {
  const drawer = scope.matches?.('[data-cart-drawer]') ? scope : scope.querySelector('[data-cart-drawer]');
  if (!drawer || drawer.dataset.drawerReady === 'true') return;
  drawer.dataset.drawerReady = 'true';
  const panel = drawer.querySelector('.cart-drawer__panel');
  let returnFocus = null;
  const open = () => { returnFocus = document.activeElement; drawer.classList.add('is-open'); drawer.setAttribute('aria-hidden', 'false'); document.body.classList.add('cart-drawer-open'); window.setTimeout(() => panel?.focus(), 50); };
  const close = () => { drawer.classList.remove('is-open'); drawer.setAttribute('aria-hidden', 'true'); document.body.classList.remove('cart-drawer-open'); returnFocus?.focus?.(); };
  drawer.querySelectorAll('[data-cart-drawer-close]').forEach((button) => button.addEventListener('click', close));
  drawer.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;
    const focusable = [...drawer.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),textarea:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])')].filter((item) => item.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });
  drawer.openCartDrawer = open;
};

const refreshCartDrawer = async (openAfter = false) => {
  try {
    const response = await fetch(`${cartRoot()}?sections=cart-drawer`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart drawer could not be refreshed');
    const sections = await response.json();
    const parsed = new DOMParser().parseFromString(sections['cart-drawer'] || '', 'text/html');
    const replacement = parsed.querySelector('[data-cart-drawer]');
    const current = document.querySelector('[data-cart-drawer]');
    if (replacement && current) { current.replaceWith(replacement); initCartDrawer(replacement); initPackageProtection(replacement); if (openAfter) replacement.openCartDrawer?.(); }
  } catch { if (openAfter) window.location.assign(cartRoot() + 'cart'); }
};

const changeCartDrawerLine = async (control, quantity) => {
  const row = control.closest('[data-cart-drawer-item]');
  const drawer = control.closest('[data-cart-drawer]');
  const lineKey = row?.dataset.cartLineKey;
  if (!row || !drawer || !lineKey || quantity < 0) return;
  if (row.dataset.protectionLine === 'true' && quantity === 0) setProtectionDismissed(Number(row.dataset.protectionVariantId), true);
  const items = drawer.querySelector('.cart-drawer__items');
  items?.classList.add('is-loading');
  drawer.querySelectorAll('[data-cart-drawer-decrease], [data-cart-drawer-increase], [data-cart-drawer-remove]').forEach((button) => { button.disabled = true; });
  try {
    const response = await fetch(cartRoot() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity })
    });
    if (!response.ok) throw new Error('Cart could not be updated');
    const cart = await response.json();
    updateCartIndicators(cart);
    document.dispatchEvent(new CustomEvent('jacamar:cart-updated', { detail: { cart, openDrawer: true } }));
  } catch {
    items?.classList.remove('is-loading');
    drawer.querySelectorAll('[data-cart-drawer-decrease], [data-cart-drawer-increase], [data-cart-drawer-remove]').forEach((button) => { button.disabled = false; });
    window.location.assign(cartRoot() + 'cart');
  }
};

initCartDrawer();
document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-cart-link]');
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!link || !drawer) return;
  event.preventDefault();
  drawer.openCartDrawer?.();
});
document.addEventListener('click', (event) => {
  const control = event.target.closest('[data-cart-drawer-decrease], [data-cart-drawer-increase], [data-cart-drawer-remove]');
  if (!control) return;
  const row = control.closest('[data-cart-drawer-item]');
  const currentQuantity = Number(row?.dataset.cartLineQuantity || 0);
  const nextQuantity = control.matches('[data-cart-drawer-remove]') ? 0 : Math.max(0, currentQuantity + (control.matches('[data-cart-drawer-increase]') ? 1 : -1));
  changeCartDrawerLine(control, nextQuantity);
});
document.addEventListener('keydown', (event) => { if (event.key === 'Escape') document.querySelector('[data-cart-drawer].is-open [data-cart-drawer-close]')?.click(); });
document.addEventListener('jacamar:cart-updated', (event) => {
  const drawerIsOpen = Boolean(document.querySelector('[data-cart-drawer].is-open'));
  const openAfter = event.detail?.openDrawer === false ? drawerIsOpen : true;
  refreshCartDrawer(openAfter);
});
